import React from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "../../../../../components/PhoneInput";
import Input from "../../../../../components/Input";
import Switch from "../../../../../components/Switch";
import Dropdown from "../../../../../components/Dropdown";
import TextArea from "../../../../../components/TextArea";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";
import { Validation } from "../../../../../interfaces/common";
import { AddEdit as AddEditInterface } from "../../../../../interfaces/user.interface";
import { Tooltip } from "@mui/material";

interface UserDetailsProps {
  edit: boolean;
  view?: boolean;
  managers: any[];
  roles: any[];
  allRoles: any[];
  allManagers: any[];
  formData: AddEditInterface;
  validation: Validation;
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  onKeyDown: (e: any, name?: string) => void;
  onInputBlur: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
  onPermissionMetricsClick: () => void;
}

const UserDetails: React.FC<UserDetailsProps> = ({
  edit, view, managers, roles, allRoles, allManagers, formData, validation, dropdownSearchData, onDropdownSearchChange, onKeyDown, onInputBlur, onInputChange, onPermissionMetricsClick
}) => {
  const { t } = useTranslation()

  return (
    <form className="card-body" autoComplete="off">
      <div className="row g-6">
        <h6 className="mb-0">
          1. {t("users.addEdit.pageTexts.form.sections.primaryDetails")}
        </h6>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.firstName}
              name={VALIDATION_FIELD_MAPPING.firstName}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.firstName)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.firstName)?.message ?? ''}
              label={t("users.addEdit.pageTexts.form.firstName.label")}
              placeholder={t("users.addEdit.pageTexts.form.firstName.placeholder")}
              value={formData.firstName}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.firstName)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.firstName)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.lastName}
              name={VALIDATION_FIELD_MAPPING.lastName}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.lastName)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.lastName)?.message ?? ''}
              label={t("users.addEdit.pageTexts.form.lastName.label")}
              placeholder={t("users.addEdit.pageTexts.form.lastName.placeholder")}
              value={formData.lastName}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.lastName)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.lastName)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="input-group input-group-merge">
            <div className="form-floating form-floating-outline">
              <Input
                required
                type="text"
                id={VALIDATION_FIELD_MAPPING.email}
                name={VALIDATION_FIELD_MAPPING.email}
                disabled={edit || view}
                error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.email)}
                errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.email)?.message ?? ''}
                label={t("users.addEdit.pageTexts.form.email.label")}
                placeholder={t("users.addEdit.pageTexts.form.email.placeholder")}
                value={formData.email}
                onKeyDown={onKeyDown}
                onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.email)}
                onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.email)}
              />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <PhoneInput
              type="text"
              required
              defaultCountry="CA"
              id={VALIDATION_FIELD_MAPPING.phoneNumber}
              name={VALIDATION_FIELD_MAPPING.phoneNumber}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.phoneNumber)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.phoneNumber)?.message ?? ''}
              label={t("users.addEdit.pageTexts.form.phoneNumber.label")}
              placeholder={t("users.addEdit.pageTexts.form.phoneNumber.placeholder")}
              value={(formData.countryCode || "") + (formData.phoneNumber || "")}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.phoneNumber)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.phoneNumber)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="d-flex gap-2">
            <div className="form-floating form-floating-outline flex-grow-1">
              <Dropdown
                values={formData.role && formData.role.toString().trim() ? [formData.role] : []}
                required
                options={(roles || []).map((role: any) => ({
                  label: role.name,
                  value: role.id,
                }))}
                allOptions={(allRoles || []).map((role: any) => ({
                  label: role.name,
                  value: role.id,
                }))}
                id={VALIDATION_FIELD_MAPPING.role}
                name={VALIDATION_FIELD_MAPPING.role}
                disabled={view}
                searchText={dropdownSearchData.role}
                onSearch={onDropdownSearchChange}
                placeholder={t("users.addEdit.pageTexts.form.role.placeholder")}
                label={t("users.addEdit.pageTexts.form.role.label")}
                error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.role)}
                errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.role)?.message ?? ''}
                onChange={onInputChange}
                onBlur={onInputBlur}
              />
            </div>
            <div className="d-flex align-items-end">
              <Tooltip
                title={t('users.addEdit.pageTexts.permissionMatrix.title')}
                arrow
                classes={{
                  tooltip: 'tooltip',
                }}
              >
                <i className="ri-shield-user-line ri-22px btn btn-outline-primary px-3 py-2" onClick={onPermissionMetricsClick}></i>
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.manager && formData.manager.toString().trim() ? [formData.manager] : []}
              options={(managers || []).map((manager: any) => ({
                label: manager.name,
                value: manager.id,
              }))}
              allOptions={(allManagers || []).map((manager: any) => ({
                label: manager.name,
                value: manager.id,
              }))}
              id={VALIDATION_FIELD_MAPPING.manager}
              name={VALIDATION_FIELD_MAPPING.manager}
              disabled={view}
              searchText={dropdownSearchData.manager}
              onSearch={onDropdownSearchChange}
              placeholder={t("users.addEdit.pageTexts.form.manager.placeholder")}
              label={t("users.addEdit.pageTexts.form.manager.label")}
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">
          2. {t("users.addEdit.pageTexts.form.sections.otherInformation")}
        </h6>

        <div className="col-md-9">
          <div className="form-floating form-floating-outline">
            <TextArea
              id={VALIDATION_FIELD_MAPPING.notes}
              name={VALIDATION_FIELD_MAPPING.notes}
              disabled={view}
              error={!!validation.fields.find(f => f.field === VALIDATION_FIELD_MAPPING.notes)}
              errorMessage={validation.fields.find(f => f.field === VALIDATION_FIELD_MAPPING.notes)?.message ?? ""}
              label={t("users.addEdit.pageTexts.form.notes.label")}
              placeholder={t("users.addEdit.pageTexts.form.notes.placeholder")}
              value={formData.notes}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.notes)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.notes)}
              rows={4}
              className="form-control-lg h-100"
            />
          </div>
        </div>

        <div className="col-md-3 d-flex align-items-start">
          <div className="form-floating form-floating-outline">
            <Switch
              label={formData.status ? t("users.addEdit.pageTexts.form.status.activeLabel") : t("users.addEdit.pageTexts.form.status.inActiveLabel")}
              checked={formData.status}
              name={VALIDATION_FIELD_MAPPING.status}
              disabled={view}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.status)}
            />
          </div>
        </div>
      </div>
    </form>
  )
};

export default UserDetails;